"use client";

import React, { useState } from "react";
import { Blocks, Clock, Users, Compass, ArrowRight, Sparkles, Zap, Cpu, CircleCheck as CheckCircle2, BookOpen, ChevronRight, ShieldCheck, GraduationCap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface CourseCardData {
  code: string;
  nameVi: string;
  nameEn: string;
  ageVi: string;
  ageEn: string;
  platform: string;
  durationVi: string;
  durationEn: string;
  groupVi: string;
  groupEn: string;
  descVi: string;
  descEn: string;
  skillsVi: string[];
  skillsEn: string[];
  specialNoteVi?: string;
  specialNoteEn?: string;
  isBridge?: boolean;
  ctaLink: string;
}
const DUPLO_COURSES: CourseCardData[] = [{
  code: "D1",
  nameVi: "Bé làm kỹ sư",
  nameEn: "Little Engineer",
  ageVi: "4–5 tuổi",
  ageEn: "4–5 years",
  platform: "Duplo",
  durationVi: "12 buổi × 90 phút",
  durationEn: "12 sessions × 90 mins",
  groupVi: "2–3 học sinh/bộ kit",
  groupEn: "2–3 students/kit",
  descVi: "Làm quen với chi tiết lắp ráp, đọc hình theo từng bước, kết cấu vững và các chuyển động cơ bản.",
  descEn: "Get familiar with modular assembly, step-by-step diagram reading, sturdy structural building, and basic mechanical motions.",
  skillsVi: ["Lắp ráp theo hình", "Kết cấu vững", "Quay", "Lăn", "Gập mở", "Thứ tự", "Thẻ mũi tên", "Lặp lại", "Sửa lỗi đơn giản"],
  skillsEn: ["Diagram assembly", "Sturdy structures", "Rotation", "Rolling", "Folding", "Sequencing", "Arrow cards", "Repetition", "Basic debugging"],
  specialNoteVi: "Tư duy thuật toán D1 hoàn toàn không cần máy tính (Unplugged).",
  specialNoteEn: "D1 computational thinking is completely screen-free (Unplugged).",
  ctaLink: "/RoboticsCourseD1Page"
}, {
  code: "D2",
  nameVi: "Thành phố của em",
  nameEn: "My Future City",
  ageVi: "4–5 tuổi (sau D1) / 5 tuổi",
  ageEn: "4–5 years (after D1) / 5 years",
  platform: "Duplo",
  durationVi: "12 buổi × 90 phút",
  durationEn: "12 sessions × 90 mins",
  groupVi: "2–3 học sinh/bộ kit",
  groupEn: "2–3 students/kit",
  descVi: "Khám phá các cơ cấu chuyển động thành phố, cầu bập bênh, thanh nâng và xây dựng chuỗi lệnh có điều kiện.",
  descEn: "Explore urban mechanical movements, lever lifts, gears, and navigate routing sequences with conditional logic.",
  skillsVi: ["Bánh răng", "Đòn bẩy", "Cơ cấu nâng – hạ", "Chuỗi lệnh 4–6 bước", "Vòng lặp", "Nếu… thì…", "Tìm đường", "Debugging"],
  skillsEn: ["Gears", "Levers", "Lifting mechanisms", "4–6 step sequences", "Loops", "If... then...", "Pathfinding", "Debugging"],
  ctaLink: "/RoboticsCourseD2Page"
}, {
  code: "D3",
  nameVi: "Cỗ máy kỳ diệu",
  nameEn: "Marvelous Machines",
  ageVi: "5–6 tuổi",
  ageEn: "5–6 years",
  platform: "Duplo",
  durationVi: "12 buổi × 90 phút",
  durationEn: "12 sessions × 90 mins",
  groupVi: "2–3 học sinh/bộ kit",
  groupEn: "2–3 students/kit",
  descVi: "Nâng cao cơ chế truyền lực phức tạp, thanh truyền, tay gắp, cơ cấu bắn và sơ đồ khối thuật toán trực quan.",
  descEn: "Advance into complex force transmission, linkages, grippers, launcher mechanisms, and visual algorithm flowcharts.",
  skillsVi: ["Tay quay", "Thanh truyền", "Cơ cấu nâng", "Tay gắp", "Cơ cấu bắn", "Bánh xích", "Flowchart bằng hình", "Vòng lặp có điều kiện", "Đặt tên chuỗi lệnh", "Debugging có hệ thống"],
  skillsEn: ["Cranks", "Linkages", "Lift structures", "Grippers", "Launchers", "Track belts", "Picture flowcharts", "Conditional loops", "Sequence naming", "Systematic debugging"],
  ctaLink: "/RoboticsCourseD3Page"
}, {
  code: "D4",
  nameVi: "Động cơ nhí",
  nameEn: "Little Motors & Energy",
  ageVi: "5–6 tuổi",
  ageEn: "5–6 years",
  platform: "Duplo",
  durationVi: "12 buổi × 90 phút",
  durationEn: "12 sessions × 90 mins",
  groupVi: "2–3 học sinh/bộ kit",
  groupEn: "2–3 students/kit",
  descVi: "Bước ngoặt kích hoạt năng lượng: làm chủ pin, động cơ điện, công tắc đảo chiều và tư duy Nhập → Xử lý → Xuất.",
  descEn: "Electrification breakthrough: master battery packs, DC motors, direction switches, and the Input → Process → Output model.",
  skillsVi: ["Pin & Năng lượng", "Công tắc", "Động cơ điện", "Quay thuận / ngược", "Timed command cards", "Input → Process → Output", "Lặp lại", "Nếu… thì…"],
  skillsEn: ["Battery & Power", "Switches", "Electric motors", "CW / CCW rotation", "Timed command cards", "Input → Process → Output", "Repetition", "If... then..."],
  isBridge: true,
  specialNoteVi: "CẦU NỐI SANG ROBOTICS LẬP TRÌNH (WHALESBOT)",
  specialNoteEn: "BRIDGE TO PROGRAMMABLE ROBOTICS (WHALESBOT)",
  ctaLink: "/RoboticsCourseD4Page"
}];
const PROGRESSION_STEPS = [{
  vi: "LẮP RÁP",
  en: "CONSTRUCTION",
  subVi: "Hình khối & Kết cấu vững",
  subEn: "3D Forms & Sturdiness",
  icon: Blocks
}, {
  vi: "CƠ CẤU",
  en: "MECHANISMS",
  subVi: "Bánh răng, đòn bẩy, tay quay",
  subEn: "Gears, Levers, Cranks",
  icon: Compass
}, {
  vi: "TƯ DUY THUẬT TOÁN",
  en: "COMPUTATIONAL LOGIC",
  subVi: "Thứ tự lệnh & Debugging",
  subEn: "Sequences & Debugging",
  icon: BookOpen
}, {
  vi: "ĐIỆN & ĐỘNG CƠ",
  en: "POWER & MOTORS",
  subVi: "Pin, công tắc, đảo chiều",
  subEn: "Batteries, Switches, Gears",
  icon: Zap
}, {
  vi: "INPUT / PROCESS / OUTPUT",
  en: "INPUT / PROCESS / OUTPUT",
  subVi: "Mô hình xử lý tín hiệu",
  subEn: "Signal Processing System",
  icon: Cpu
}, {
  vi: "ROBOT LẬP TRÌNH",
  en: "PROGRAMMABLE ROBOT",
  subVi: "Sẵn sàng lên HP1 WhalesBot",
  subEn: "Ready for HP1 WhalesBot",
  icon: Sparkles
}];
export default function DuploFoundationTrack() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  return <section id="duplofoundationtrack" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 border-b border-border overflow-hidden">
      {/* Decorative architectural background hints */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-40 bg-[radial-gradient(ellipse_at_top,_var(--primary)_0%,_transparent_55%)] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 top-20 w-96 h-96 rounded-full bg-accent/15 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -left-24 bottom-20 w-96 h-96 rounded-full bg-secondary/15 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Language switch and stage badge */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 border-b border-border/70">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-card text-foreground px-3 py-1 font-semibold tracking-wide border-border shadow-xs uppercase">
              <Blocks className="size-3.5 text-primary" />
              {lang === "vi" ? "Giai Đoạn 01 • 4–6 Tuổi" : "Stage 01 • Ages 4–6"}
            </Badge>
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              LEGO® DUPLO EDUCATION
            </Badge>
          </div>

          <div className="flex items-center rounded-lg border border-border bg-card p-1 shadow-2xs">
            <Button type="button" variant="ghost" size="sm" onClick={() => setLang("vi")} className={`h-auto px-3 py-1 text-xs font-semibold rounded-md transition-colors ${lang === "vi" ? "bg-primary text-primary-foreground shadow-xs hover:bg-primary hover:text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Tiếng Việt
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setLang("en")} className={`h-auto px-3 py-1 text-xs font-semibold rounded-md transition-colors ${lang === "en" ? "bg-primary text-primary-foreground shadow-xs hover:bg-primary hover:text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              English
            </Button>
          </div>
        </div>

        {/* Section Headline with LEGO Duplo STEAM Kit Photo */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
              {lang === "vi" ? "GIAI ĐOẠN 1 — NỀN TẢNG ROBOTICS 4–6 TUỔI" : "STAGE 1 — ROBOTICS FOUNDATION AGES 4–6"}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              {lang === "vi" ? "Xây nền tảng tư duy kỹ thuật, cơ khí và tư duy lập trình trước khi bước vào robot lập trình." : "Building early engineering mindset, mechanical principles, and computational logic prior to entering programmable robotics."}
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-xl border border-border bg-card p-2 shadow-md overflow-hidden group">
              <div className="relative aspect-16/10 rounded-lg overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/lego_duplo_steam_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/lego_duplo_steam_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/lego_duplo_steam_kit.png-wvc-sizes" alt="LEGO Duplo STEAM building equipment and bricks for early robotics foundation" loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-transparent pointer-events-none" />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white text-xs font-medium">
                  <span className="bg-primary text-primary-foreground px-2.5 py-1 rounded-md font-semibold text-[11px] tracking-wide uppercase shadow-xs">
                    LEGO® DUPLO STEAM
                  </span>
                  <span className="bg-slate-900/80 backdrop-blur-xs text-slate-200 px-2 py-0.5 rounded text-[10px] font-mono border border-slate-700/50">
                    AGES 4–6 • HARDWARE
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4 Cards Grid: D1 to D4 */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {DUPLO_COURSES.map((course, idx) => <Card key={course.code} data-index={idx} className={`relative flex flex-col justify-between transition-all duration-200 border-2 shadow-sm hover:shadow-md hover:-translate-y-1 ${course.isBridge ? "border-primary bg-card/90 ring-2 ring-primary/20" : "border-border bg-card"}`}>
              {course.isBridge && <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-11/12 text-center">
                  <span className="inline-block bg-primary text-primary-foreground text-[10px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full shadow-xs whitespace-nowrap">
                    {lang === "vi" ? "CẦU NỐI SANG ROBOTICS LẬP TRÌNH" : "BRIDGE TO PROGRAMMABLE ROBOTICS"}
                  </span>
                </div>}

              <CardHeader className="pt-7 pb-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center size-9 rounded-lg bg-primary/10 text-primary font-bold text-base tracking-tight">
                      {course.code}
                    </span>
                    <Badge variant="outline" className="border-border text-xs font-semibold">
                      {course.platform}
                    </Badge>
                  </div>
                  <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                    {lang === "vi" ? course.ageVi : course.ageEn}
                  </span>
                </div>

                <CardTitle className="mt-4 text-xl font-bold tracking-tight text-foreground font-serif">
                  {lang === "vi" ? course.nameVi : course.nameEn}
                </CardTitle>

                {/* Logistics */}
                <div className="mt-3 space-y-1.5 text-xs text-muted-foreground border-y border-border/60 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <Clock className="size-3.5 text-primary shrink-0" />
                    <span>{lang === "vi" ? course.durationVi : course.durationEn}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users className="size-3.5 text-secondary shrink-0" />
                    <span>{lang === "vi" ? course.groupVi : course.groupEn}</span>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col justify-between pb-6 pt-0 space-y-5">
                <div>
                  <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed min-h-[4rem]">
                    {lang === "vi" ? course.descVi : course.descEn}
                  </p>

                  {course.specialNoteVi && <div className="mt-3 p-2.5 rounded-md bg-accent/15 border border-accent/30 text-[11px] text-foreground font-medium flex items-start gap-1.5">
                      <Sparkles className="size-3.5 text-accent shrink-0 mt-0.5" />
                      <span>{lang === "vi" ? course.specialNoteVi : course.specialNoteEn}</span>
                    </div>}

                  {/* Skills chips */}
                  <div className="mt-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                      {lang === "vi" ? "Kỹ năng trọng tâm:" : "Key Skills Acquired:"}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(lang === "vi" ? course.skillsVi : course.skillsEn).map((skill, sIdx) => <span key={sIdx} className="inline-block text-[11px] bg-muted text-muted-foreground hover:text-foreground px-2 py-0.5 rounded-md border border-border/50 transition-colors">
                          {skill}
                        </span>)}
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <Button asChild variant={course.isBridge ? "default" : "outline"} className="w-full justify-center group font-medium" size="sm">
                    <Link to={course.ctaLink}>
                      <span>
                        {lang === "vi" ? `XEM CHI TIẾT ${course.code}` : `VIEW ${course.code} DETAILS`}
                      </span>
                      <ArrowRight className="size-3.5 ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>)}
        </div>

        {/* Section 6: SKILL PROGRESSION AFTER D1-D4 */}
        <div className="mt-20 rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-sm relative overflow-hidden">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-widest">
              <GraduationCap className="size-4" />
              {lang === "vi" ? "Lộ trình chuyển đổi năng lực" : "Competency Evolution"}
            </div>
            <h3 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight font-serif text-foreground">
              {lang === "vi" ? "SAU D4, CON ĐÃ SẴN SÀNG BƯỚC VÀO ROBOTICS LẬP TRÌNH" : "AFTER D4, STUDENTS ARE FULLY READY FOR PROGRAMMABLE ROBOTICS"}
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              {lang === "vi" ? "Bốn khóa Duplo xây chắc từng lớp tư duy — từ xúc giác lắp ghép đến vận hành hệ thống tự động hoàn chỉnh." : "The four Duplo courses systematically stack cognitive layers — from tactile construction to complete autonomous control."}
            </p>
          </div>

          {/* Stepped Progression Sequence */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-2 relative">
            {PROGRESSION_STEPS.map((step, idx) => {
            const StepIcon = step.icon;
            const isLast = idx === PROGRESSION_STEPS.length - 1;
            return <div key={idx} data-index={idx} className={`relative p-4 rounded-xl border flex flex-col justify-between transition-all ${isLast ? "bg-primary text-primary-foreground border-primary shadow-md" : "bg-background text-foreground border-border hover:border-primary/40"}`}>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-xs font-mono font-bold px-1.5 py-0.5 rounded ${isLast ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                        0{idx + 1}
                      </span>
                      <StepIcon className={`size-4 ${isLast ? "text-primary-foreground" : "text-primary"}`} />
                    </div>
                    <div className="font-bold text-xs sm:text-sm tracking-tight leading-snug">
                      {lang === "vi" ? step.vi : step.en}
                    </div>
                  </div>

                  <div className={`mt-3 text-[11px] leading-tight ${isLast ? "text-primary-foreground/85" : "text-muted-foreground"}`}>
                    {lang === "vi" ? step.subVi : step.subEn}
                  </div>

                  {!isLast && <div aria-hidden="true" className="hidden lg:flex absolute -right-2 top-1/2 -translate-y-1/2 z-20 text-muted-foreground/60">
                      <ChevronRight className="size-4" />
                    </div>}
                </div>;
          })}
          </div>
        </div>

        {/* Section 7: D4 -> HP1 TRANSITION */}
        <div className="mt-12 rounded-2xl bg-gradient-to-br from-card via-card to-muted border border-border p-6 sm:p-10 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="px-3 py-1 font-semibold text-xs tracking-wide">
                  <Zap className="size-3 text-primary mr-1" />
                  {lang === "vi" ? "Chuyển tiếp trọng tâm" : "Core Curriculum Milestone"}
                </Badge>
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
                {lang === "vi" ? "TỪ MÔ HÌNH CƠ KHÍ ĐẾN ROBOT CÓ THỂ LẬP TRÌNH" : "FROM MECHANICAL MODELS TO PROGRAMMABLE ROBOTS"}
              </h3>

              <p className="text-sm sm:text-base text-foreground/80 leading-relaxed">
                {lang === "vi" ? "Khóa D4 (Động cơ nhí) chính là cây cầu chuyển tiếp vững chắc. Sau D4, các bé 6–7 tuổi đã quen thuộc với cấu trúc động cơ, chuyển động cơ khí và chuỗi lệnh tuần hoàn sẽ tự tin bước tiếp vào HP1 – Kỹ sư cơ khí nhí (WhalesBot AI Module 1S)." : "Course D4 (Little Motors) forms the definitive springboard. Upon completing D4, young learners understand motors, dynamic mechanisms, and algorithmic execution, stepping effortlessly into HP1 – Junior Mechanical Engineer with WhalesBot AI Module 1S."}
              </p>

              {/* Assessment disclaimer box */}
              <div className="p-4 rounded-xl bg-background border border-border/80 flex items-start gap-3">
                <ShieldCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm text-foreground/85 leading-relaxed">
                  <span className="font-semibold text-foreground">
                    {lang === "vi" ? "Dành cho học sinh từ 7 tuổi: " : "For Students Aged 7+: "}
                  </span>
                  {lang === "vi" ? "Không bắt buộc mọi học sinh 7 tuổi phải học lại từ D1–D4. ROBOTO tổ chức buổi 'Kiểm tra đầu vào' (Entrance Assessment) đánh giá năng lực tư duy không gian và logic máy tính để xếp lớp thẳng vào HP1 nếu trẻ đã đạt độ sẵn sàng." : "Completion of D1–D4 is not mandatory for 7+ newcomers. ROBOTO conducts an Entrance Assessment to evaluate spatial reasoning and logical foundations, allowing direct entry into HP1 when readiness is established."}
                </div>
              </div>
            </div>

            <div className="lg:col-span-4 flex flex-col gap-3 justify-center bg-background/80 p-6 rounded-xl border border-border">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {lang === "vi" ? "Lựa chọn bước tiếp theo" : "Next Milestone Actions"}
              </div>

              <Button asChild size="lg" className="w-full justify-between shadow-xs">
                <Link to="/FreeTrialContactPage">
                  <span>{lang === "vi" ? "Đăng ký kiểm tra đầu vào" : "Book Entrance Assessment"}</span>
                  <CheckCircle2 className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full justify-between">
                <Link to="/LearningPathCurriculumPage">
                  <span>{lang === "vi" ? "Khám phá chặng HP1–HP6" : "Explore HP1–HP6 Track"}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <p className="text-[11px] text-center text-muted-foreground mt-1">
                {lang === "vi" ? "Tư vấn lộ trình cá nhân hóa hoàn toàn miễn phí" : "Complimentary tailored pathway consultation"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}